/**
 * Tagada Rx — where the affiliate's commission is remitted.
 *
 * The rail drives the fields: US rails need routing + account, an
 * international wire needs IBAN + SWIFT. The backend only persists the
 * fields the selected rail uses, so switching rails cannot leave a stale
 * account number behind.
 *
 * The account number is never sent back to the browser — once saved, the
 * form shows the stored last 4 and asks for a full re-entry to change it.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Landmark, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import {
  useRxSavePayoutBankAccount,
  type RxPayoutMethod,
  type RxPayoutProfile,
} from '@/lib/api/rx';

const METHOD_KEYS: Record<RxPayoutMethod, string> = {
  ach: 'bankAccountForm.method.ach',
  domesticWire: 'bankAccountForm.method.domesticWire',
  internationalWire: 'bankAccountForm.method.internationalWire',
  check: 'bankAccountForm.method.check',
};

export function RxBankAccountForm({
  profile,
  onSaved,
}: {
  profile: RxPayoutProfile;
  onSaved: () => void;
}) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const save = useRxSavePayoutBankAccount();
  const stored = profile.profile.bankDetails;

  const [method, setMethod] = React.useState<RxPayoutMethod>(
    (profile.profile.payoutMethod as RxPayoutMethod) ?? 'ach',
  );
  const [beneficiaryName, setBeneficiaryName] = React.useState(stored?.beneficiaryName ?? '');
  const [bankName, setBankName] = React.useState(stored?.bankName ?? '');
  const [routingNumber, setRoutingNumber] = React.useState(stored?.routingNumber ?? '');
  const [accountNumber, setAccountNumber] = React.useState('');
  const [accountType, setAccountType] = React.useState<'checking' | 'savings'>(
    stored?.accountType ?? 'checking',
  );
  const [iban, setIban] = React.useState('');
  const [swiftCode, setSwiftCode] = React.useState(stored?.swiftCode ?? '');

  const usesUsRails = method === 'ach' || method === 'domesticWire';
  const usesIban = method === 'internationalWire';

  const canSubmit =
    beneficiaryName.trim().length >= 2 &&
    (usesUsRails
      ? /^\d{9}$/.test(routingNumber.trim()) && accountNumber.trim().length >= 4
      : usesIban
        ? iban.replace(/\s/g, '').length >= 15 && swiftCode.trim().length >= 8
        : true);

  const handleSave = async () => {
    try {
      await save.mutateAsync({
        method,
        currency: profile.profile.payoutCurrency || 'USD',
        beneficiaryName: beneficiaryName.trim(),
        bankName: bankName.trim() || undefined,
        routingNumber: usesUsRails ? routingNumber.trim() : undefined,
        accountNumber: usesUsRails ? accountNumber.trim() : undefined,
        accountType: usesUsRails ? accountType : undefined,
        iban: usesIban ? iban.replace(/\s/g, '') : undefined,
        swiftCode: usesIban ? swiftCode.trim() : undefined,
      });
      setAccountNumber('');
      setIban('');
      onSaved();
      toast({ title: t('bankAccountForm.toast.saved.title') });
    } catch (err) {
      toast({
        title: t('bankAccountForm.toast.failed.title'),
        description: err instanceof Error ? err.message : t('bankAccountForm.toast.unknownError'),
        variant: 'destructive',
      });
    }
  };

  const savedLast4 = stored?.accountNumberLast4 ?? stored?.ibanLast4;

  return (
    <div className="space-y-4">
      {profile.bankReady && savedLast4 ? (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
          <div className="text-sm">
            <div className="font-semibold text-emerald-800">
              {t('bankAccountForm.savedTo', { last4: savedLast4 })}
            </div>
            <div className="mt-0.5 text-xs text-emerald-700">
              {stored?.beneficiaryName} · {t(METHOD_KEYS[method])}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border px-4 py-3">
          <Landmark className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
          <p className="text-muted-foreground text-xs leading-relaxed">
            {t('bankAccountForm.thirdPartyNotice')}
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="rx-payout-method">{t('bankAccountForm.fields.method')}</Label>
          <Select value={method} onValueChange={(v) => setMethod(v as RxPayoutMethod)}>
            <SelectTrigger id="rx-payout-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(METHOD_KEYS) as RxPayoutMethod[]).map((m) => (
                <SelectItem key={m} value={m}>
                  {t(METHOD_KEYS[m])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="rx-beneficiary">{t('bankAccountForm.fields.beneficiary')}</Label>
          <Input
            id="rx-beneficiary"
            value={beneficiaryName}
            onChange={(e) => setBeneficiaryName(e.target.value)}
            placeholder={t('bankAccountForm.placeholders.beneficiary')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rx-bank-name">{t('bankAccountForm.fields.bank')}</Label>
          <Input
            id="rx-bank-name"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder={t('bankAccountForm.placeholders.bankOptional')}
          />
        </div>

        {usesUsRails ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="rx-routing">{t('bankAccountForm.fields.routing')}</Label>
              <Input
                id="rx-routing"
                value={routingNumber}
                onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                placeholder={t('bankAccountForm.placeholders.routing')}
                inputMode="numeric"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rx-account">{t('bankAccountForm.fields.account')}</Label>
              <Input
                id="rx-account"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.trim())}
                placeholder={
                  savedLast4
                    ? t('bankAccountForm.placeholders.savedReenter', { last4: savedLast4 })
                    : ''
                }
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rx-account-type">{t('bankAccountForm.fields.accountType')}</Label>
              <Select
                value={accountType}
                onValueChange={(v) => setAccountType(v as 'checking' | 'savings')}
              >
                <SelectTrigger id="rx-account-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">{t('bankAccountForm.accountType.checking')}</SelectItem>
                  <SelectItem value="savings">{t('bankAccountForm.accountType.savings')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        ) : null}

        {usesIban ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="rx-iban">{t('bankAccountForm.fields.iban')}</Label>
              <Input
                id="rx-iban"
                value={iban}
                onChange={(e) => setIban(e.target.value.toUpperCase())}
                placeholder={
                  savedLast4
                    ? t('bankAccountForm.placeholders.savedReenter', { last4: savedLast4 })
                    : ''
                }
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rx-swift">{t('bankAccountForm.fields.swift')}</Label>
              <Input
                id="rx-swift"
                value={swiftCode}
                onChange={(e) => setSwiftCode(e.target.value.toUpperCase())}
                placeholder={t('bankAccountForm.placeholders.swift')}
              />
            </div>
          </>
        ) : null}
      </div>

      <Button onClick={handleSave} disabled={!canSubmit || save.isPending} size="sm">
        {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {profile.bankReady ? t('bankAccountForm.submit.update') : t('bankAccountForm.submit.create')}
      </Button>
    </div>
  );
}
